import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Card, Dropdown, Form, Modal, Spinner } from 'react-bootstrap'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css'
import 'bootstrap/dist/css/bootstrap.min.css'
import { LuGraduationCap } from 'react-icons/lu'
import {
  BsArrowClockwise,
  BsCheckLg,
  BsThreeDotsVertical,
  BsTrash,
  BsXLg,
} from 'react-icons/bs'
import toast from 'react-hot-toast'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import ApiEndpoints from './commonComponents/ApiEndpoints'
import ExpandableReasonText from './commonComponents/ExpandableReasonText'

const STATUS_FILTER_OPTIONS = [
  { label: 'All', value: 'ALL' },
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Canceled', value: 'canceled' },
]

const STATUS_CONFIG = {
  pending: { label: 'Pending', bg: '#FFFBEB', color: '#92400E', dot: '#F59E0B' },
  approved: { label: 'Approved', bg: '#ECFDF5', color: '#065F46', dot: '#10B981' },
  canceled: { label: 'Canceled', bg: '#FEF2F2', color: '#991B1B', dot: '#EF4444' },
}

const StatusBadge = ({ status }) => {
  const key = String(status || 'pending').toLowerCase()
  const cfg = STATUS_CONFIG[key] || STATUS_CONFIG.pending
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding: '3px 10px',
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.3px',
        background: cfg.bg,
        color: cfg.color,
        fontFamily: 'Inter, system-ui, sans-serif',
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          background: cfg.dot,
          flexShrink: 0,
        }}
      />
      {cfg.label}
    </span>
  )
}

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return '—'
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return dateStr
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const extractLeaveList = (response) => {
  const body = response?.data
  if (Array.isArray(body?.data)) return body.data
  if (Array.isArray(body)) return body
  return []
}

const getApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data
  if (data?.message) return data.message
  if (data?.errors && typeof data.errors === 'object') {
    const firstError = Object.values(data.errors).flat()[0]
    if (firstError) return firstError
  }
  return fallback
}

const isTeacherLeave = (item) =>
  String(item?.user_type || '').toLowerCase() === 'teacher'

export default function ViewLeave() {
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const { logo } = useInstitute()

  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const [leaves, setLeaves] = useState([])
  const [loading, setLoading] = useState(true)
  const [updatingId, setUpdatingId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [leaveToDelete, setLeaveToDelete] = useState(null)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const fetchLeaves = useCallback(async () => {
    setLoading(true)
    try {
      const params =
        statusFilter !== 'ALL' ? { status: statusFilter } : undefined
      const response = await ApiEndpoints.getAllTeacherLeaves(params)
      const teacherLeaves = extractLeaveList(response).filter(isTeacherLeave)
      setLeaves(teacherLeaves)
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load leave requests.'))
      setLeaves([])
    } finally {
      setLoading(false)
    }
  }, [statusFilter])

  useEffect(() => {
    fetchLeaves()
  }, [fetchLeaves])

  const filteredLeaves = useMemo(() => leaves, [leaves])

  const getTeacherName = (item) => item.user_name || '—'
  const getTeacherEmail = (item) => item.user_email || '—'
  const getLeaveReason = (item) => item.leave_description || '—'
  const getLeaveFrom = (item) => item.from_date
  const getLeaveTo = (item) => item.to_date
  const getLeaveStatus = (item) => String(item.status || 'pending').toLowerCase()

  const handleUpdateStatus = async (leave, status) => {
    const leaveId = leave.id
    if (!leaveId) {
      toast.error('Leave ID not found.')
      return
    }

    setUpdatingId(leaveId)
    try {
      const response = await ApiEndpoints.updateTeacherLeaveStatus(leaveId, {
        status,
      })
      toast.success(
        response.data?.message ||
          `Leave ${status === 'approved' ? 'approved' : 'canceled'} successfully.`,
      )
      fetchLeaves()
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update leave status.'))
    } finally {
      setUpdatingId(null)
    }
  }

  const handleDeleteLeave = async () => {
    const leaveId = leaveToDelete?.id
    if (!leaveId) return

    setDeletingId(leaveId)
    try {
      const response = await ApiEndpoints.deleteLeaveRequest(leaveId)
      toast.success(response.data?.message || 'Leave request deleted successfully.')
      setLeaveToDelete(null)
      fetchLeaves()
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete leave request.'))
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <>
      <style>
        {`
          .dropdown-toggle-no-caret::after {
            display: none !important;
          }
        `}
      </style>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                padding: '12px 28px',
                borderBottom: '1px solid #D1D3D4',
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#fff' }} />
                  )}
                </div>
                <h3
                  className="m-0 fw-bold"
                  style={{
                    color: '#223843',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  View Leave
                </h3>
              </div>
              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            padding: isCollapsed ? '32px' : '40px',
          }}
        >
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
            <div
              className="d-flex align-items-center"
              style={{
                backgroundColor: '#ffffff',
                padding: '8px 16px',
                borderRadius: '8px',
                gap: '8px',
                border: '1px solid #D1D3D4',
              }}
            >
              <span
                style={{
                  color: '#6b7280',
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                Status:
              </span>
              <Form.Select
                className="w-auto"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                disabled={loading}
                style={{
                  borderRadius: '6px',
                  border: '1px solid #D1D3D4',
                  backgroundColor: '#ffffff',
                  color: '#111827',
                  fontSize: '13px',
                  fontWeight: '500',
                  padding: '4px 32px 4px 12px',
                  minWidth: '140px',
                  cursor: 'pointer',
                  height: '36px',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                {STATUS_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </div>

            <button
              type="button"
              onClick={fetchLeaves}
              disabled={loading}
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: '#10B981',
                border: 'none',
                color: '#fff',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Refresh"
            >
              <BsArrowClockwise size={20} />
            </button>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" style={{ color: '#223843' }} />
            </div>
          ) : (
            <Card style={{ border: '1px solid #D1D3D4', borderRadius: 12 }}>
              <Table className="table mb-0 text-start">
                <Thead>
                  <Tr style={{ backgroundColor: '#F3F4F6' }}>
                    <Th>Teacher Name</Th>
                    <Th>Teacher Email</Th>
                    <Th>Reason for leave</Th>
                    <Th>From Date</Th>
                    <Th>To Date</Th>
                    <Th>Status</Th>
                    <Th className="text-center">Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredLeaves.length > 0 ? (
                    filteredLeaves.map((leave, index) => {
                      const leaveId = leave.id
                      const status = getLeaveStatus(leave)
                      const isPending = status === 'pending'
                      const isUpdating = updatingId === leaveId

                      return (
                        <Tr
                          key={leaveId || index}
                          style={{
                            backgroundColor:
                              index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)',
                          }}
                        >
                          <Td>{getTeacherName(leave)}</Td>
                          <Td>{getTeacherEmail(leave)}</Td>
                          <Td
                            style={{
                              minWidth: isMobile ? 'auto' : '180px',
                              maxWidth: isMobile ? '100%' : '340px',
                              verticalAlign: 'top',
                              padding: isMobile ? '12px 10px' : '12px 16px',
                            }}
                          >
                            <ExpandableReasonText
                              text={getLeaveReason(leave)}
                              isMobile={isMobile}
                              modalTitle="Leave Details"
                              modalDetails={[
                                { label: 'Teacher Name', value: getTeacherName(leave) },
                                { label: 'Teacher Email', value: getTeacherEmail(leave) },
                                {
                                  label: 'From Date',
                                  value: formatDisplayDate(getLeaveFrom(leave)),
                                },
                                {
                                  label: 'To Date',
                                  value: formatDisplayDate(getLeaveTo(leave)),
                                },
                                {
                                  label: 'Status',
                                  value:
                                    getLeaveStatus(leave).charAt(0).toUpperCase() +
                                    getLeaveStatus(leave).slice(1),
                                },
                              ]}
                            />
                          </Td>
                          <Td>{formatDisplayDate(getLeaveFrom(leave))}</Td>
                          <Td>{formatDisplayDate(getLeaveTo(leave))}</Td>
                          <Td>
                            <StatusBadge status={status} />
                          </Td>
                          <Td className="text-center">
                            {isPending ? (
                              <Dropdown align="end">
                                <Dropdown.Toggle
                                  variant="light"
                                  className="dropdown-toggle-no-caret"
                                  disabled={isUpdating || deletingId === leaveId}
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    backgroundColor: 'transparent',
                                    border: '1px solid #D1D3D4',
                                    padding: '0',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <BsThreeDotsVertical />
                                </Dropdown.Toggle>
                                <Dropdown.Menu
                                  style={{
                                    borderRadius: '8px',
                                    border: '1px solid #D1D3D4',
                                    padding: '8px',
                                    minWidth: '160px',
                                  }}
                                >
                                  <Dropdown.Item
                                    onClick={() =>
                                      handleUpdateStatus(leave, 'approved')
                                    }
                                    style={{
                                      fontSize: '13px',
                                      padding: '10px 12px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                    }}
                                  >
                                    <BsCheckLg size={16} /> Approve
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() =>
                                      handleUpdateStatus(leave, 'canceled')
                                    }
                                    style={{
                                      fontSize: '13px',
                                      padding: '10px 12px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                      color: '#dc3545',
                                    }}
                                  >
                                    <BsXLg size={16} /> Cancel
                                  </Dropdown.Item>
                                  <Dropdown.Divider style={{ margin: '4px 0' }} />
                                  {/* <Dropdown.Item
                                    onClick={() => setLeaveToDelete(leave)}
                                    style={{
                                      fontSize: '13px',
                                      padding: '10px 12px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                      color: '#dc3545',
                                    }}
                                  >
                                    <BsTrash size={16} /> Delete
                                  </Dropdown.Item> */}
                                </Dropdown.Menu>
                              </Dropdown>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </Td>
                        </Tr>
                      )
                    })
                  ) : (
                    <Tr>
                      <Td colSpan={7} className="text-center py-4 text-muted">
                        No leave requests found.
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Card>
          )}
        </div>
      </div>

      <Modal
        show={Boolean(leaveToDelete)}
        onHide={() => !deletingId && setLeaveToDelete(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
            Delete Leave Request
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
          Are you sure you want to delete this pending leave request for{' '}
          <strong>{leaveToDelete?.user_name || 'this teacher'}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            onClick={() => setLeaveToDelete(null)}
            disabled={Boolean(deletingId)}
            style={{
              borderRadius: '8px',
              padding: '8px 16px',
              border: '1px solid #D1D3D4',
              backgroundColor: '#ffffff',
              color: '#223843',
              cursor: deletingId ? 'not-allowed' : 'pointer',
            }}
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleDeleteLeave}
            disabled={Boolean(deletingId)}
            style={{
              borderRadius: '8px',
              padding: '8px 16px',
              border: 'none',
              backgroundColor: '#dc3545',
              color: '#ffffff',
              cursor: deletingId ? 'not-allowed' : 'pointer',
              opacity: deletingId ? 0.7 : 1,
            }}
          >
            {deletingId ? 'Deleting...' : 'Delete'}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
